"use client";

import React from "react";
import { Megaphone, MessageCircle, CalendarCheck, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  id: 1,
  title: "Data Aggregation",
  description: "We ingest raw economic data from the ABS, RBA, and private sector partners, ensuring a comprehensive foundation for every index calculation.",
  icon: Megaphone
}, {
  id: 2,
  title: "Seasonal Adjustment",
  description: "Our algorithms apply rigorous seasonal smoothing to remove cyclical noise, revealing the underlying economic trends affecting Australian households.",
  icon: MessageCircle
}, {
  id: 3,
  title: "Weighting & Review",
  description: "Data is weighted by demographic relevance and subjected to peer review by our senior economists before final publication to the Media Centre.",
  icon: CalendarCheck
}];
export default function The3StepPatientJourney() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223380" id="the-science-of-indices" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(hsl(var(--primary)/0.03)_1px,transparent_1px)] [background-size:32px_32px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-primary tracking-tight mb-4">
            The Science of Indices
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Our institutional-grade methodology ensures every data point is accurate, independent, and ready for national policy discussion.
          </p>
        </div>

        <div ref={ref} className={`relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="hidden md:block absolute top-[3.5rem] left-[16%] right-[16%] h-0.5 border-t border-dashed border-border animate-pulse z-0" />

          {}
          <div className="md:hidden absolute top-[10%] bottom-[10%] left-[3.25rem] w-0.5 border-l border-dashed border-border animate-pulse z-0" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={step.id} data-index={i} className="relative z-10 flex flex-col items-start md:items-center text-left md:text-center group">
                  <div className="flex flex-row md:flex-col items-center md:items-center gap-6 md:gap-6 w-full">
                    {}
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 bg-accent rounded-none blur-lg group-hover:bg-accent/80 transition-colors duration-500" />
                      <div className="relative w-20 h-20 md:w-28 md:h-28 rounded-none bg-background border border-border flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-500">
                        <Icon className="w-8 h-8 md:w-12 md:h-12 text-primary" strokeWidth={1.5} />
                        
                        {}
                        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-none bg-primary text-primary-foreground flex items-center justify-center font-bold font-mono text-xs shadow-sm border border-background">
                          0{step.id}
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="flex-1 md:w-full">
                      <div className="inline-block px-2 py-1 mb-3 bg-secondary text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border">
                        Technical Phase
                      </div>
                      <h3 className="text-xl md:text-2xl font-semibold font-default text-foreground mb-3">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground font-default leading-relaxed text-sm md:text-base">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-none px-8 font-default uppercase tracking-wider text-xs">
            <Link to="/MethodologyPage">
              Full Methodology <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}